import React, { createContext, useContext, useState, useEffect } from 'react';

const StateContext = createContext();

const initialState = {
  chat: false,
  cart: false,
  userProfile: false,
  notification: false,
};

// ── Rumoh Pangan Aceh Theme Defaults ───────────────────────────────────────────
// Warna utama: #1a1a18 (dark brown/black) dan #c9862a (gold/tan)
const rumohPanganDefaults = {
  color: '#c9862a', // Gold accent sebagai default
  mode: 'Light',    // Light mode default
};

// Palet warna tema Rumoh Pangan Aceh
export const rumohPanganColors = [
  { name: 'Gold', color: '#c9862a' },
  { name: 'Dark Brown', color: '#1a1a18' },
  { name: 'Earth Brown', color: '#a16207' },
  { name: 'Forest Green', color: '#059669' },
  { name: 'Leaf Green', color: '#16a34a' },
  { name: 'Nature Teal', color: '#0d9488' },
  { name: 'Warm Beige', color: '#d4a853' },
  { name: 'Sage Green', color: '#6b8e6b' },
];

export const ContextProvider = ({ children }) => {
  // Initialize state with Rumoh Pangan Aceh theme defaults
  const [screenSize, setScreenSize] = useState(undefined);
  const [currentColor, setCurrentColor] = useState(() => {
    const savedColor = localStorage.getItem('rumohPanganColorMode');
    return savedColor || rumohPanganDefaults.color;
  });

  const [currentMode, setCurrentMode] = useState(() => {
    const savedMode = localStorage.getItem('rumohPanganThemeMode');
    return savedMode || rumohPanganDefaults.mode;
  });

  const [themeSettings, setThemeSettings] = useState(false);
  const [activeMenu, setActiveMenu] = useState(true);
  const [isClicked, setIsClicked] = useState(initialState);

  // Load saved preferences on component mount
  useEffect(() => {
    const savedColor = localStorage.getItem('rumohPanganColorMode');
    const savedMode = localStorage.getItem('rumohPanganThemeMode');

    if (savedColor) {
      setCurrentColor(savedColor);
    }
    if (savedMode) {
      setCurrentMode(savedMode);
    }
  }, []);

  const setMode = (e) => {
    const newMode = e.target.value;
    setCurrentMode(newMode);
    localStorage.setItem('rumohPanganThemeMode', newMode);

    // Apply theme class to document
    if (newMode === 'Dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const setColor = (color) => {
    setCurrentColor(color);
    localStorage.setItem('rumohPanganColorMode', color);
    console.log(`Rumoh Pangan Aceh: Theme color changed to ${color}`);
  };

  const handleClick = (clicked) => {
    setIsClicked((prevState) => ({
      ...initialState,
      [clicked]: !prevState[clicked],
    }));
  };

  // Helper function untuk mendapatkan warna dengan opacity
  const getColorWithOpacity = (color, opacity) => {
    if (!color) return `rgba(201, 134, 42, ${opacity})`;
    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  };

  // Context value
  const contextValue = {
    // Original properties
    currentColor,
    currentMode,
    activeMenu,
    screenSize,
    setScreenSize,
    handleClick,
    isClicked,
    initialState,
    setIsClicked,
    setActiveMenu,
    setCurrentColor,
    setCurrentMode,
    setMode,
    setColor,
    themeSettings,
    setThemeSettings,

    // Rumoh Pangan Aceh specific properties
    rumohPanganColors,
    rumohPanganDefaults,
    getColorWithOpacity,
  };

  return (
    <StateContext.Provider value={contextValue}>
      {children}
    </StateContext.Provider>
  );
};

export const useStateContext = () => {
  const context = useContext(StateContext);
  if (!context) {
    throw new Error('useStateContext must be used within a ContextProvider');
  }
  return context;
};

// Additional utility hooks
export const useRumohPanganTheme = () => {
  const {
    currentColor,
    currentMode,
    setColor,
    setMode,
    rumohPanganColors,
    rumohPanganDefaults,
    getColorWithOpacity,
  } = useStateContext();

  return {
    primaryColor: currentColor,
    themeMode: currentMode,
    setPrimaryColor: setColor,
    setThemeMode: setMode,
    colorPalette: rumohPanganColors,
    defaults: rumohPanganDefaults,
    withOpacity: getColorWithOpacity,
  };
};
